import React, { useSyncExternalStore } from "react";
import { isDebug } from "@base/BuildEnvironment";
import { IResiStore, IResiStoreGet, ListenerFn, UpdateFn } from "@base/resi/IResiStore";
/* eslint-disable react-hooks/rules-of-hooks */
import { CompareUtil, EqualsFunctionOption } from "@base/util/CompareUtil";

const checkEqualityFns = isDebug(); // false

const withDevTools =
  // process.env.NODE_ENV === 'development' &&
  typeof window !== "undefined" && (window as any).__REDUX_DEVTOOLS_EXTENSION__;

if (withDevTools) console.log("With DevTools");

type Listener<S> = { fn: ListenerFn<S>; deleted: boolean; debugName?: string };

function callListeners<S>(listeners: Listener<S>[], state: S): void {
  const cloned = [...listeners];
  for (const l of cloned) {
    if (!l.deleted) l.fn(state);
  }
}

function subscribeListener<S>(listeners: Listener<S>[], listenerFunction: ListenerFn<S>, debugName?: string): () => void {
  const listener: Listener<S> = {
    fn: listenerFunction,
    deleted: false,
    debugName
  };
  // if (debugName)
  //  console.log("[Resi] listener.push ", debugName);
  listeners.push(listener);
  return () => {
    // if (debugName)
    //  console.log("[Resi] listener.deleted ", debugName);
    listener.deleted = true;
  };
}

export function observeStore<State extends object, Selection>(
  store: IResiStoreGet<State>,
  select: (state: State) => Selection,
  onChange: (state: Selection, prevState: Selection | undefined) => void,
  options?: { equalityFn?: EqualsFunctionOption; debugName?: string }
): () => void {
  let currentState: Selection | undefined;

  function handleChange(): void {
    const nextState = select(store.get());
    const eqFn = CompareUtil.getEqualsFunction(options?.equalityFn);
    if (!eqFn(nextState, currentState)) {
      // if ((store as any).name)
      //   console.log("ObserveStore", (store as any).name)
      // if (!shallowEqual(nextState, currentState)) {
      const prevState = currentState;
      currentState = nextState;
      onChange(currentState, prevState);
    }
  }

  const unsubscribe = store.subscribe(handleChange, options?.debugName);
  handleChange();
  return unsubscribe;
}

class _ResiStore<S extends object> implements IResiStore<S> {
  private state: S;
  private listeners: Listener<S>[] = [];
  private devTools: any;
  public name: string;

  public constructor(name: string, initialState: S) {
    this.name = name;
    this.state = initialState;

    if (withDevTools) {
      this.devTools = (window as any).__REDUX_DEVTOOLS_EXTENSION__.connect({ name, trace: true, traceLimit: 25 });
      this.devTools.subscribe((message: any) => {
        if (message.type === "DISPATCH") {
          this.#set(JSON.parse(message.state));
          this.callListeners();
        }
        console.log("Message", message);
      });
      this.devTools.init(initialState);
    }
  }

  public get(): S {
    return this.state;
  }

  public set(stateOrFn: UpdateFn<S> | S, options?: { devToolsAction?: string; skipListeners?: boolean }): S {
    const stateToSet = typeof stateOrFn === "function" ? (stateOrFn as UpdateFn<S>)(this.state) : stateOrFn;
    if (stateToSet !== this.state) {
      if (withDevTools) this.devTools.send(options?.devToolsAction || "set", stateToSet);
      this.#set(stateToSet, options?.skipListeners);
    }
    return stateToSet;
  }

  #set(stateOrFn: UpdateFn<S> | S, skipListeners?: boolean): void {
    const stateToSet = typeof stateOrFn === "function" ? (stateOrFn as UpdateFn<S>)(this.state) : stateOrFn;
    this.state = stateToSet;
    if (!skipListeners)
      this.callListeners();
  }

  callListeners(): void {
    this.listeners = this.listeners.filter((l) => !l.deleted);
    callListeners<S>(this.listeners, this.state); // don't rerender if 3 useSelectors of same state are used...
  }

  public useSelector<R>(selector: (state: S) => R, options?: { equalityFn?: EqualsFunctionOption; debugName?: string }): R {
    const isEqual = CompareUtil.getEqualsFunction(options?.equalityFn)

    const cachedState = React.useRef<{ state: S; selected: R, selector: (state: S) => R }>({ state: this.state, selected: selector(this.state), selector });

    const subscribe: (onStoreChange: () => void) => (() => void) = (onStoreChange) => subscribeListener(this.listeners, onStoreChange, options?.debugName);
    try {
      const ret = useSyncExternalStore(subscribe, () => {
        const cached = cachedState.current;
        if (selector === cached.selector && this.state === cached.state) {
          if (checkEqualityFns) {
            const newSelection = selector(this.state);
            if (!isEqual(newSelection, cached.selected)) {
              console.warn("State differs - please check you equlityFn", options?.debugName, newSelection, cached.selected);
            }
          }
          return cached.selected;
        }
        cached.selector = selector;
        cached.state = this.state;

        const newSelection = selector(this.state);
        if (!isEqual(newSelection, cached.selected)) {
          cached.selected = newSelection;
        }
        return cached.selected;
      }, undefined);
      // const ret = useSyncExternalStoreWithSelector(subscribe, get, null, selector, isEqual)
      // console.log("Ret", ret, retA)
      return ret;
    } catch (e) {
      console.warn("useSyncExternalStoreWithSelector fired", e)
      throw e;
    }
  }

  public subscribe(listenerFunction: ListenerFn<S>, debugName?: string): () => void {
    return subscribeListener(this.listeners, listenerFunction, debugName);
  }
}

export function createResiStore<S extends object>(name: string, initialState: S, hot?: any): IResiStore<S> {
  const store = new _ResiStore(name, initialState);
  if (hot) {
      hot.dispose((data: any) => {
        data[name] = store.get();
      })
      if (hot.data?.[name])
        store.set(hot.data[name]);
  }
  return store;
}
